import React from 'react';
import { Link } from 'react-router-dom';
import { useCreatorRankBadge, CreatorRankBadge as BadgeData } from '@/hooks/useCreatorRankBadge';
import './CreatorRankBadge.scss';

interface Props {
  username: string | null | undefined;
  /** Optional pre-fetched badge — bypass the hook. Useful when a
   *  parent already has the whole page's badges (e.g. an SSR path
   *  that we might add later) and doesn't want to re-request. */
  badge?: BadgeData | null;
  className?: string;
}

/**
 * Compact reputation chip that sits inline with a creator's name in
 * feed / discovery / marketplace surfaces. Renders either:
 *   - "Top N%"  — when the population is big enough to be meaningful
 *                 AND the creator is in the top half.
 *   - "#N"      — when we can't confidently show percentile (small
 *                 population) or the top-half guard trims percentile.
 *   - nothing   — creator has no computed reputation, or is bottom-
 *                 half in a small population where "#N" would look
 *                 like a demotion.
 *
 * The badge is deliberately quiet by default — we don't want to
 * highlight every creator in a feed. It surfaces the wedge signal
 * ("this creator has real reputation") without shouting.
 */
const CreatorRankBadge: React.FC<Props> = ({ username, badge: pre, className = '' }) => {
  const hookBadge = useCreatorRankBadge(pre === undefined ? username : null);
  const badge = pre !== undefined ? pre : hookBadge;

  if (badge === undefined) return null; // loading — render nothing rather than a flicker
  if (badge === null)      return null;

  // Prefer "Top N%" when we have percentile AND it's top-half. Otherwise
  // fall back to "#N" — but only when the position is genuinely noteworthy
  // (top 20 by absolute rank in any population, or top 10% in a bigger one).
  const label = pickBadgeLabel(badge);
  if (!label) return null;

  // Deep-link to the leaderboard this badge is derived from so the
  // viewer can see the full context (who else is ranked, where the
  // caller sits, etc.). stopPropagation prevents the click from
  // bubbling to a parent card that might navigate to the profile
  // (PostCard has clickable regions around this badge).
  return (
    <Link
      to="/leaderboards/most-reputable"
      className={`creator-rank-badge ${className}`.trim()}
      title={`${label} on Klyx by KlyxValue — click to view the leaderboard`}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
};

function pickBadgeLabel(b: BadgeData): string | null {
  if (b.percentile !== null && b.percentile <= 0.5) {
    const pct = Math.max(1, Math.round(b.percentile * 100));
    return `Top ${pct}%`;
  }
  // Small-population fallback — show "#N" only when it reads as an
  // achievement rather than a demotion. Top 20 absolute OR top-half
  // in a population of at least 10.
  if (b.position <= 20 || (b.total >= 10 && b.position <= b.total / 2)) {
    return `#${b.position}`;
  }
  return null;
}

export default CreatorRankBadge;
